import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import '../../css/forms/ApplicationForms.css';

const AcornsAmbassadorManager = () => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    university: '',
    graduation_year: '',
    school_email: '',
    phone_number: '',
    instagram_url: '',
    tiktok_url: '',
    linkedin_url: '',
    campus_marketing_exp: '',
    leadership_exp: '',
    time_commitment: '',
    resume_url: '',
    cover_letter_url: ''
  });

  const [formErrors, setFormErrors] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [showThankYouModal, setShowThankYouModal] = useState(false);  

  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({ ...prev, [name]: value }));

    // Real-time validation for email
    if (name === 'school_email') {
      if (!value) {
        setFormErrors(prev => ({ ...prev, school_email: 'Email is required' }));
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/i.test(value)) {
        setFormErrors(prev => ({ ...prev, school_email: 'Please use a valid email address' }));
      } else {
        setFormErrors(prev => ({ ...prev, school_email: '' }));
      }
    } else if (submitAttempted && formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};
    const requiredFields = [
      'first_name', 'last_name', 'university', 'graduation_year',
      'school_email', 'phone_number', 'campus_marketing_exp',
      'leadership_exp', 'time_commitment', 'resume_url'
    ];

    requiredFields.forEach(field => {
      if (!formData[field] || (typeof formData[field] === 'string' && !formData[field].trim())) {
        errors[field] = 'This field is required';
      }
    });

    // Email validation
    if (!formData.school_email) {
      errors.school_email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/i.test(formData.school_email)) {
      errors.school_email = 'Please use a valid email address';
    }

    // Basic URL validation for resume (just checks if it looks like a URL)
    if (!formData.resume_url) {
      errors.resume_url = 'Please provide a link to your resume';
    } else if (!/\..{2,}/.test(formData.resume_url)) { // Simple check for a dot and TLD
      errors.resume_url = 'Please provide a valid URL';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitAttempted(true);

    const isValid = validateForm();
    if (!isValid) {
      // Find the first error field and scroll to it
      const firstErrorField = document.querySelector('.error');
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
        firstErrorField.focus();
      }
      return;
    }

    try {
      const response = await fetch('https://rm-backend-service-505761517154.us-central1.run.app/submit-ambassador-manager', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          firstName: formData.first_name,
          lastName: formData.last_name,
          email: formData.school_email,
          phone: formData.phone_number,
          university: formData.university,
          graduationYear: formData.graduation_year,
          instagram: formData.instagram_url || '',
          tiktok: formData.tiktok_url || '',
          linkedin: formData.linkedin_url || '',
          campusMarketingExp: formData.campus_marketing_exp,
          leadershipExp: formData.leadership_exp,
          timeCommitment: formData.time_commitment,
          resumeUrl: formData.resume_url,
          coverLetterUrl: formData.cover_letter_url || ''
        }),
      });

      const contentType = response.headers.get("content-type");

      if (!response.ok) {
        if (contentType && contentType.includes("application/json")) {
          const errorJson = await response.json();
          throw new Error(errorJson.message || 'Failed to submit application');
        } else {
          const errorText = await response.text();
          throw new Error(`Server error: ${errorText.slice(0, 100)}...`);
        }
      }

      const data = await response.json();
      console.log('Success:', data);
      setShowThankYouModal(true);

    } catch (error) {
      console.error('Submission error:', error.message);
      alert(`Error submitting form: ${error.message}`);
    }
  };

  const closeModal = () => {
    setShowThankYouModal(false);
    navigate('/roles');
    window.scrollTo(0, 0);
  };

  return (
    <div className="aam-container">
      <Helmet>
        <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap" 
        rel="stylesheet" />
      </Helmet>

      {showThankYouModal && (
        <div className="thank-you-modal">
          <div className="thank-you-content">
            <h1>Thank You for Applying!</h1>
            <p>We've received your application for the Campus Ambassador Manager position.</p>
            <p>If we feel you're a good fit for the role, we'll reach out with next steps soon.</p>
            <button onClick={closeModal}>Go to Roles</button>
          </div>
        </div>
      )}

      <div className="aam-header">
        <h1>Campus Ambassador Manager</h1>
        <div className="aam-meta">
          <span className="meta-item">Location: Remote</span>
          <span className="meta-item">Job Type: Part-Time</span>
        </div>
      </div>

      <div className="aam-content">
        <section className="aam-section aam-about">
          <h2>About Us</h2>
          <p>At Romulus Media, we specialize in leveraging student networks to deliver innovative marketing solutions. We are committed to providing valuable educational and professional opportunities to our network members, enhancing their career trajectories.</p>
        </section>

        <section className="aam-section aam-description">
          <h2>Job Description</h2>
          <p>Romulus Media is seeking high-performing, organized, and people-first student leaders to serve as Ambassador Program Managers. This role is ideal for those who excel at rallying teams, leading marketing campaigns, and managing communication pipelines. Managers will be trained to operate like account leads—overseeing ambassador recruitment, performance tracking, campaign execution, and client reporting for major national brands.</p>
          <p className="aam-emphasis">This is not a posting or promotional role. This is a strategic, leadership position where you will be at the helm of a full ambassador team on your managed campuses.</p>
        </section>

        <section className="aam-section aam-responsibilities">
          <h2>Core Responsibilities (60 hours/month)</h2>
          <ul className="aam-list">
            <li>Lead ambassador operations on your assigned campus for specific brand campaigns.</li>
            <li>Recruit, vet, and onboard student ambassadors according to campaign needs.</li>
            <li>Manage weekly communication, tasks, and accountability systems for your ambassador team.</li>
            <li>Coordinate digital and in-person marketing activities, including tabling, flyering, posting schedules, and classroom drops.</li>
            <li>Ensure campaign compliance with university rules and brand guidelines.</li>
            <li>Track and report performance using standardized Romulus Media tools (e.g., spreadsheets, weekly summaries).</li>
            <li>Serve as the main point of contact between Romulus Media and your campuses.</li>
          </ul>
        </section>

        <section className="aam-section aam-candidate">
          <h2>Ideal Candidate</h2>
          <ul className="aam-list">
            <li>Has prior experience as a student ambassador, RA, club president, or other student leadership role.</li>
            <li>Extremely organized and self-motivated.</li>
            <li>Capable of managing people and solving problems independently.</li>
            <li>Enjoys mentorship and coaching others.</li>
            <li>Strong communicator with good judgment and a sense of ownership.</li>
          </ul>
        </section>

        <section className="aam-section aam-benefits">
          <h2>Benefits</h2>
          <ul className="aam-list">
            <li>$2,000/month depending on campaign scope and experience (with bonuses for team performance).</li>
            <li>Extensive leadership experience that is highly relevant for marketing, consulting, or operations careers.</li>
            <li>Opportunity to work directly with national brands and Romulus Media leadership.</li>
            <li>Priority access to future job opportunities within Romulus Media and our clients.</li>
          </ul>
        </section>

        <section className="aam-section aam-application">
          <h2>How to Apply</h2>
          <p>Submit your resume and a short note describing why you'd be a great fit, plus links to any social media profiles or past leadership examples. Applications are reviewed on a rolling basis.</p>
          <p>If you have any questions or concerns, please email <a href="mailto:elisa@romulusmedia.com" className="aam-link">elisa@romulusmedia.com</a></p>
        </section>

        <form onSubmit={handleSubmit} className="aam-form">
          <h2 className="form-title">Application Form</h2>

          {submitAttempted && Object.keys(formErrors).length > 0 && (
            <div className="form-error-message">
              Please fill all required fields!
            </div>
          )}

          <div className="form-grid">
            <div className="form-group">
              <label className="question-text">First Name *</label>
              <input 
                type="text" 
                name="first_name" 
                value={formData.first_name} 
                onChange={handleChange} 
                className={formErrors.first_name ? 'error' : ''}
              />
              {formErrors.first_name && <span className="error-message">{formErrors.first_name}</span>}
            </div>

            <div className="form-group">
              <label className="question-text">Last Name *</label>
              <input 
                type="text" 
                name="last_name" 
                value={formData.last_name} 
                onChange={handleChange} 
                className={formErrors.last_name ? 'error' : ''}
              />
              {formErrors.last_name && <span className="error-message">{formErrors.last_name}</span>}
            </div>

            <div className="form-group">
              <label className="question-text">University/College Name *</label>
              <input 
                type="text" 
                name="university" 
                value={formData.university} 
                onChange={handleChange} 
                className={formErrors.university ? 'error' : ''}
              />
              {formErrors.university && <span className="error-message">{formErrors.university}</span>}
            </div>

            <div className="form-group">
              <label className="question-text">Year of Graduation *</label>
              <input 
                type="text" 
                name="graduation_year" 
                value={formData.graduation_year} 
                onChange={handleChange} 
                className={formErrors.graduation_year ? 'error' : ''}
              />
              {formErrors.graduation_year && <span className="error-message">{formErrors.graduation_year}</span>}
            </div>

            <div className="form-group">
              <label className="question-text">Email *</label>
              <input 
                type="email" 
                name="school_email" 
                value={formData.school_email} 
                onChange={handleChange} 
                className={formErrors.school_email ? 'error' : ''}
                placeholder="yourname@example.com"
              />
              {formErrors.school_email && (
                <span className="error-message">
                  {!formData.school_email 
                    ? 'Email is required' 
                    : 'Please enter a valid email address'
                  }
                </span>
              )}
            </div>

            <div className="form-group">
              <label className="question-text">Phone Number *</label>
              <input 
                type="tel" 
                name="phone_number" 
                value={formData.phone_number} 
                onChange={handleChange} 
                className={formErrors.phone_number ? 'error' : ''}
              />
              {formErrors.phone_number && <span className="error-message">{formErrors.phone_number}</span>}
            </div>

            <div className="form-group">
              <label className="question-text">Instagram (link not handle, e.g, instagram.com/johndoe) (optional)</label>
              <input 
                type="text" 
                name="instagram_url" 
                value={formData.instagram_url} 
                onChange={handleChange} 
                placeholder="instagram.com/johndoe"
              />
            </div>

            <div className="form-group">
              <label className="question-text">TikTok (link not handle, e.g, tiktok.com/@johndoe) (optional)</label>
              <input 
                type="text" 
                name="tiktok_url" 
                value={formData.tiktok_url} 
                onChange={handleChange} 
                placeholder="tiktok.com/@johndoe"
              />
            </div>

            <div className="form-group">
              <label className="question-text">LinkedIn (link not handle, e.g, linkedin.com/in/johndoe) (optional)</label>
              <input 
                type="text" 
                name="linkedin_url" 
                value={formData.linkedin_url} 
                onChange={handleChange} 
                placeholder="linkedin.com/in/johndoe"
              />
            </div>
            
            <div className="form-group">
              <label className="question-text">Resume URL *</label>
              <input 
                type="text" 
                name="resume_url" 
                value={formData.resume_url} 
                onChange={handleChange} 
                className={formErrors.resume_url ? 'error' : ''}
                placeholder="drive.google.com/your-resume"
              />
              {formErrors.resume_url && <span className="error-message">{formErrors.resume_url}</span>}
              <p className="form-help-text">Upload your resume to Google Drive, Dropbox, or similar service and share the public link here.</p>
            </div>

            <div className="form-group">
              <label className="question-text">Cover letter + references URL (optional)</label>
              <input 
                type="text" 
                name="cover_letter_url" 
                value={formData.cover_letter_url} 
                onChange={handleChange} 
                placeholder="drive.google.com/your-cover-letter"
              />
              <p className="form-help-text">Upload your cover letter to Google Drive, Dropbox, or similar service and share the public link here. 
                For multiple links, separate with commas.
              </p>
            </div>

            <div className="form-group full-width">
              <label className="question-text">Have you previously participated in any 
                Campus Marketing programs or ambassador roles? If so, which company 
                or organization did you work with, what were your responsibilities, 
                and how many hours per week did you typically dedicate to the role? *</label>
              <textarea 
                name="campus_marketing_exp" 
                value={formData.campus_marketing_exp} 
                onChange={handleChange} 
                className={formErrors.campus_marketing_exp ? 'error' : ''}
                rows="3"
              />
              {formErrors.campus_marketing_exp && <span className="error-message">{formErrors.campus_marketing_exp}</span>}
            </div>

            <div className="form-group full-width">
              <label className="question-text">Have you held a leadership role before that included quota requirements? *</label>
              <textarea 
                name="leadership_exp" 
                value={formData.leadership_exp} 
                onChange={handleChange} 
                className={formErrors.leadership_exp ? 'error' : ''}
                rows="3"
              />
              {formErrors.leadership_exp && <span className="error-message">{formErrors.leadership_exp}</span>}
            </div>

            <div className="form-group full-width">
              <label className="question-text">Can you meet the 15h/week requirement for the duration of this role? *</label>
              <textarea 
                name="time_commitment" 
                value={formData.time_commitment} 
                onChange={handleChange} 
                className={formErrors.time_commitment ? 'error' : ''}
                rows="3"
              />
              {formErrors.time_commitment && <span className="error-message">{formErrors.time_commitment}</span>}
            </div>
          </div>

          <button type="submit" className="submit-btn">Submit Application</button>
        </form>
      </div>
    </div>
  );
};

export default AcornsAmbassadorManager;